import { useRef, useState, forwardRef, useImperativeHandle } from "react";

const Filter = forwardRef(({ records = 0, search, onExportPDF, onExportExcel, onExportCSV }, ref) => {
  const searchRef = useRef();
  const [searchTerm, setSearchTerm] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);

  useImperativeHandle(ref, () => ({
    searchRef,
    searchTerm,
    setSearchTerm,
  }));

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 16px",
        background: "var(--background)",
        color: "var(--text)",
        position: "relative",
      }}
    >
      {/* LEFT SIDE */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div
          className="px-3 py-1 rounded-full text-sm font-medium"
          style={{
            background: "var(--background)",
            border: "2px solid var(--hover)",
            color: "var(--text)",
          }}
        >
          Records: {records || 0}
        </div>

        <input
          ref={searchRef}
          type="text"
          placeholder="Search Symbol"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            search?.(e.target.value);
          }}
          className="w-[180px] px-4 py-1 rounded-full text-sm focus:ring-2 outline-none"
          style={{
            background: "var(--background)",
            border: "2px solid var(--hover)",
            color: "var(--text)",
          }}
        />
      </div>

      {/* RIGHT SIDE - Export Buttons (Redesigned) */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
        {/* Main Export Trigger Button */}
        <button
          onClick={() => setShowExportMenu(!showExportMenu)}
          className="px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 shadow-sm transition-all duration-200"
          style={{
            background: "var(--primary)",
            color: "#ffffff",
            border: "none",
            cursor: "pointer",
            boxShadow: showExportMenu ? "0 0 0 3px rgba(59, 130, 246, 0.3)" : "none",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.1)")}
          onMouseLeave={(e) => (e.currentTarget.style.filter = "none")}
        >
          {/* Download Icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>

          <span>Export</span>

          {/* Chevron Dropdown Arrow */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: showExportMenu ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {/* Redesigned Export Dropdown Menu */}
        {showExportMenu && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              background: "var(--background)",
              border: "1px solid var(--border)",
              borderRadius: "12px",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              zIndex: 1000,
              minWidth: "160px",
              padding: "6px",
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            {/* Export PDF Option */}
            <button
              onClick={() => {
                onExportPDF?.();
                setShowExportMenu(false);
              }}
              className="w-full px-3 py-2 text-left text-sm rounded-lg transition-colors flex items-center gap-2.5 font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--text)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Export PDF
            </button>

            {/* Export Excel Option */}
            <button
              onClick={() => {
                onExportExcel?.();
                setShowExportMenu(false);
              }}
              className="w-full px-3 py-2 text-left text-sm rounded-lg transition-colors flex items-center gap-2.5 font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--text)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10b981"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="3" y1="15" x2="21" y2="15" />
                <line x1="9" y1="3" x2="9" y2="21" />
                <line x1="15" y1="3" x2="15" y2="21" />
              </svg>
              Export Excel
            </button>

            {/* Divider Line */}
            <div style={{ height: "1px", background: "var(--border)", margin: "4px 0" }} />

            {/* Export CSV Option */}
            <button
              onClick={() => {
                onExportCSV?.();
                setShowExportMenu(false);
              }}
              className="w-full px-3 py-2 text-left text-sm rounded-lg transition-colors flex items-center gap-2.5 font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--text)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Export CSV
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

Filter.displayName = "Filter";

export default Filter;